const DEFAULT_EVENT = 'mousedown'
const DEFAULT_TRANSITION = 600
const DEFAULT_COLOR = 'rgba(0, 0, 0, 0.35)'
const DEFAULT_Z_INDEX = '9999'

const listeners = new WeakMap()
const Ripple = {
  color: null,
  zIndex: null,
}

const getOptions = binding => {
  const options = {
    event: DEFAULT_EVENT,
    transition: DEFAULT_TRANSITION,
  }

  Object.keys(binding.modifiers || {}).forEach(modifier => {
    const transition = Number(modifier)

    if (Number.isNaN(transition)) {
      options.event = modifier
    } else {
      options.transition = transition
    }
  })

  return options
}

const getModifiersKey = binding => Object.keys(binding.modifiers || {}).sort().join('|')

const getElementSize = el => {
  const rect = el.getBoundingClientRect()

  return {
    rect,
    width: el.offsetWidth || rect.width || 0,
    height: el.offsetHeight || rect.height || 0,
  }
}

const getEventPosition = (event, width, height, rect) => {
  if (typeof event.clientX !== 'number' || typeof event.clientY !== 'number') {
    return {
      dx: width / 2,
      dy: height / 2,
    }
  }

  return {
    dx: event.clientX - rect.left,
    dy: event.clientY - rect.top,
  }
}

const removeNode = node => {
  if (node && node.parentNode) {
    node.parentNode.removeChild(node)
  }
}

const clearTargetPosition = (target, storedTargetPosition, transition) => {
  setTimeout(() => {
    const hasActiveRipple = Array.from(target.childNodes)
      .some(child => child.className === 'ripple-container')

    if (!hasActiveRipple) {
      // Directive cleanup has to restore the host element's inline style.
      // eslint-disable-next-line no-param-reassign
      target.style.position = storedTargetPosition === 'static' ? '' : storedTargetPosition
    }
  }, transition + 250)
}

const createRipple = (event, el, binding, options) => {
  const target = el
  const style = window.getComputedStyle(target)
  const borderWidth = parseInt(style.borderWidth.replace('px', ''), 10)
  const border = Number.isNaN(borderWidth) ? 0 : borderWidth
  const { rect, width, height } = getElementSize(target)
  const { dx, dy } = getEventPosition(event, width, height, rect)
  const maxX = Math.max(dx, width - dx)
  const maxY = Math.max(dy, height - dy)
  const radius = Math.sqrt((maxX * maxX) + (maxY * maxY))
  const color = binding.value || Ripple.color || DEFAULT_COLOR
  const zIndex = Ripple.zIndex || DEFAULT_Z_INDEX
  const ripple = document.createElement('div')
  const rippleContainer = document.createElement('div')
  const storedTargetPosition = target.style.position || style.position

  rippleContainer.className = 'ripple-container'
  ripple.className = 'ripple'

  ripple.style.marginTop = '0px'
  ripple.style.marginLeft = '0px'
  ripple.style.width = '1px'
  ripple.style.height = '1px'
  ripple.style.transition = `all ${options.transition}ms cubic-bezier(0.4, 0, 0.2, 1)`
  ripple.style.borderRadius = '50%'
  ripple.style.pointerEvents = 'none'
  ripple.style.position = 'relative'
  ripple.style.zIndex = zIndex
  ripple.style.backgroundColor = color

  rippleContainer.style.position = 'absolute'
  rippleContainer.style.left = `${0 - border}px`
  rippleContainer.style.top = `${0 - border}px`
  rippleContainer.style.height = '0'
  rippleContainer.style.width = '0'
  rippleContainer.style.pointerEvents = 'none'
  rippleContainer.style.overflow = 'hidden'

  if (storedTargetPosition !== 'relative') {
    target.style.position = 'relative'
  }

  rippleContainer.appendChild(ripple)
  target.appendChild(rippleContainer)

  ripple.style.marginLeft = `${dx}px`
  ripple.style.marginTop = `${dy}px`

  rippleContainer.style.width = `${width}px`
  rippleContainer.style.height = `${height}px`
  rippleContainer.style.borderTopLeftRadius = style.borderTopLeftRadius
  rippleContainer.style.borderTopRightRadius = style.borderTopRightRadius
  rippleContainer.style.borderBottomLeftRadius = style.borderBottomLeftRadius
  rippleContainer.style.borderBottomRightRadius = style.borderBottomRightRadius
  rippleContainer.style.direction = 'ltr'

  setTimeout(() => {
    ripple.style.width = `${radius * 2}px`
    ripple.style.height = `${radius * 2}px`
    ripple.style.marginLeft = `${dx - radius}px`
    ripple.style.marginTop = `${dy - radius}px`
  }, 0)

  const clearRipple = () => {
    setTimeout(() => {
      ripple.style.backgroundColor = 'rgba(0, 0, 0, 0)'
    }, 250)

    setTimeout(() => {
      removeNode(rippleContainer)
    }, 850)

    el.removeEventListener('mouseup', clearRipple, false)
    clearTargetPosition(target, storedTargetPosition, options.transition)
  }

  if (event.type === 'mousedown') {
    el.addEventListener('mouseup', clearRipple, false)
  } else {
    clearRipple()
  }
}

const unbind = el => {
  const state = listeners.get(el)

  if (!state) return

  el.removeEventListener(state.event, state.handler)
  Array.from(el.querySelectorAll('.ripple-container')).forEach(removeNode)
  listeners.delete(el)
}

const bind = (el, binding) => {
  const options = getOptions(binding)
  const handler = event => createRipple(event, el, binding, options)

  unbind(el)
  el.addEventListener(options.event, handler)
  listeners.set(el, {
    event: options.event,
    handler,
    modifiersKey: getModifiersKey(binding),
    value: binding.value,
  })
}

const updated = (el, binding) => {
  const state = listeners.get(el)

  if (!state) {
    bind(el, binding)
    return
  }

  if (state.value !== binding.value || state.modifiersKey !== getModifiersKey(binding)) {
    bind(el, binding)
  }
}

Object.assign(Ripple, {
  beforeMount: bind,
  mounted() {},
  updated,
  unmounted: unbind,
})

export default Ripple
